import React, { createRef, useMemo, useState } from 'react';

import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';

import HeaderCheckbox from './Templates/HeaderCheckbox';
import CaseTemplate from './Templates/CaseTemplate';
import UploadedByTemplate from './Templates/UploadedByTemplate';

import { TitleTemplate } from './Templates/TitleTemplate';
import SizeTemplate from './Templates/SizeTemplate';
import PagesTemplate from './Templates/PagesTemplate';
import CheckboxTemplate from './Templates/CheckboxTemplate';

import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';
import SignatureTemplate from './Templates/SignatureTemplate';
import LastEditedTemplate from './Templates/LastEditedTemplate';
import UploadedTemplate from './Templates/UploadedTemplate';
import ActionsTemplate from './Templates/ActionsTemplate/ActionsTemplate';
import GlobalActionsTemplate from './Templates/GlobalActionsTemplate/GlobalActionsTemplate';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import useFileDownload from 'modules/file_manager/hooks/useFileDownload';
import useUpdateFiles from 'modules/file_manager/hooks/useUpdateFiles';

import { paginationDropdownOptions, bytesToFormattedSize } from 'utils/utils';
import { handlePageNumberInPagination } from 'common/pagination';
import FileSharingDetailsCard from 'components/FileSharing/FileSharingDetailsCard';
import { FileSharingDetailsModal } from 'components/FileSharing/FileSharingDetailsModal';
import { isCurrentDatePastExpiry } from 'utils/utility_functions/isCurrentDatePastExpiry';
import { useDropzone } from 'react-dropzone';
import { useNavbarContext } from 'context/NavbarContext';
import { useToast } from 'context/ToastContext';
import { Auth } from 'aws-amplify';
import { createFile } from 'services/fileManager/fileManagerServices';
import { toastConstant } from 'constants/toastmessage';
import { useLocation } from 'react-router-dom';

function ListView() {
  const {
    files,
    selectedRows,
    selectAll,
    handleSingleCheckboxClick,
    handleSelectAllCheckboxClick,
    pageLimit,
    setPageLimit,
    handleGetFiles,
    onNextPageClick,
    onPrevPageClick,
    fileManagerFilters,
    currentView,
    nextToken,
    transitionToken,
    paginationDirection,
    modals,
    isClientFileManager,
    isGlobalFileManager,
    isCaseFileManager,
    isLeadFileManager,
    isFirmDocuments,
    caseDetails,
    breadCrumbs,
    setSelectedRows,
  } = useFileManagerContext();

  const { setShowBulkDeleteModal, setShowBulkMoveModal, setShowShareFileModal, setIsCopy } = modals;
  const [showFileSharingDetailsModal, setShowFileSharingDetailsModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState();
  const { downloadFile, downloadFolder } = useFileDownload();
  const { fileBulkMarkAsRead } = useUpdateFiles();

  const userContext = useUserDetailsContext();

  const { startUpload } = useNavbarContext();
  const { addToast } = useToast();
  const location = useLocation();
  const isFileManagerRoute = location.pathname === '/file-manager';

  // Global drag & drop upload
  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles?.length) return;
    const maxFiles = 20;
    if (acceptedFiles.length > maxFiles) {
      addToast(false, toastConstant.toasterType.WARN, toastConstant.header.WARNINIG, toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED);
      return;
    }
    try {
      startUpload(
        acceptedFiles,
        undefined,
        async (uploaded = []) => {
          if (!uploaded.length) return;
          let user = await Auth.currentAuthenticatedUser();
          const attachments = uploaded.map((file) => ({
            file_name: file?.file.name,
            display_name: file?.file.name,
            size: file?.file?.size.toString(),
            file_path: file?.Key,
            file_type: file?.file?.type?.toString(),
            created_by: user.attributes,
            version_id: file?.VersionId || '',
            versions: [],
            complete_path: file?.file.path?.replace(/^\/+/, ''),
          }));

          let case_id = caseDetails?.case_id ?? (isClientFileManager ? fileManagerFilters.case.id : 'undefined');

          const dataObj = {
            case_id,
            type: 'bulk_upload',
            folder_id: breadCrumbs?.[breadCrumbs.length - 1]?.value || fileManagerFilters.selectedFolder,
            file_folder: attachments,
            not_linked_to_case: false,
            is_lead: isLeadFileManager || false,
          };
          try {
            await createFile({ data: dataObj, is_lead: isLeadFileManager || false });
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.FILES_UPLOADED);
            handleGetFiles(pageLimit, breadCrumbs?.[breadCrumbs.length - 1]?.value);
          } catch (err) {
            addToast(
              false,
              toastConstant.toasterType.ERROR,
              toastConstant.api.ERROR,
              toastConstant.message.PERMISSION_DENIED_TO_UPLOAD_FILE
            );
          }
        },
        {
          caseId:
            isCaseFileManager || isLeadFileManager
              ? caseDetails?.case_id || undefined
              : isClientFileManager
              ? fileManagerFilters?.case?.id
              : undefined,
        }
      );
    } catch (err) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.FILE_UPLOAD_FAILED);
    }
  };

  // only enable dropzone when NOT on /file-manager
  const { getRootProps, isDragActive } = useDropzone({
    onDrop: !isFileManagerRoute ? onDrop : () => {},
    noClick: isFileManagerRoute,
    noKeyboard: isFileManagerRoute,
    disabled: isFileManagerRoute, // fully disables drag-and-drop
  });

  const itemsRef = useMemo(
    () =>
      Array(files && files.length ? files.length : 0)
        .fill()
        .map(() => createRef()),
    [files]
  );

  function getFilePathForPagination() {
    if (isGlobalFileManager && userContext) {
      return { module: 'file_manager', subModule: '', listName: 'self' };
    } else if (isCaseFileManager && userContext) {
      return { module: 'case', subModule: '', listName: 'file_manager' };
    } else if (isLeadFileManager && userContext) {
      return { module: 'leads', subModule: '', listName: 'file_manager' };
    } else if (isClientFileManager && userContext) {
      return { module: 'client_portals', subModule: 'filemanager', listName: 'self' };
    }
  }

  const onPageChange = (event) => {
    setPageLimit(event.value);
    let path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, event.value);
    handleGetFiles(event.value);
  };

  function nextChange() {
    let obj = {
      case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case?.id : caseDetails.case_id,
      selectedFolder: fileManagerFilters.selectedFolder,
      selectedType: fileManagerFilters.fileType,
      tag: fileManagerFilters.tag,
      query: '',
      pagination_direction: paginationDirection,
    };

    // Include shared folder parameters only for client portal
    if (isClientFileManager) {
      // Include shared_folder flag if it exists in filters
      if (fileManagerFilters?.shared_folder) {
        obj.shared_folder = fileManagerFilters.shared_folder;
      }

      // Include shared_contact_list only if it exists and has values
      if (
        fileManagerFilters?.shared_contact_list &&
        Array.isArray(fileManagerFilters.shared_contact_list) &&
        fileManagerFilters.shared_contact_list.length > 0
      ) {
        obj.shared_contact_list = fileManagerFilters.shared_contact_list;
      }

      // Include folder_shared only if it's explicitly true
      if (fileManagerFilters?.folder_shared === true) {
        obj.folder_shared = fileManagerFilters.folder_shared;
      }
    }

    onNextPageClick(obj, currentView, pageLimit);
  }

  function previousChange() {
    let obj = {
      case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case?.id : caseDetails.case_id,
      selectedFolder: fileManagerFilters.selectedFolder,
      selectedType: fileManagerFilters.fileType,
      tag: fileManagerFilters.tag,
      query: '',
      pagination_direction: paginationDirection,
    };

    // Include shared folder parameters only for client portal
    if (isClientFileManager) {
      // Include shared_folder flag if it exists in filters
      if (fileManagerFilters?.shared_folder) {
        obj.shared_folder = fileManagerFilters.shared_folder;
      }

      // Include shared_contact_list only if it exists and has values
      if (
        fileManagerFilters?.shared_contact_list &&
        Array.isArray(fileManagerFilters.shared_contact_list) &&
        fileManagerFilters.shared_contact_list.length > 0
      ) {
        obj.shared_contact_list = fileManagerFilters.shared_contact_list;
      }

      // Include folder_shared only if it's explicitly true
      if (fileManagerFilters?.folder_shared === true) {
        obj.folder_shared = fileManagerFilters.folder_shared;
      }
    }

    onPrevPageClick(obj, currentView, pageLimit);
  }

  const PaginationTemplate = {
    layout: 'RowsPerPageDropdown PrevPageLink NextPageLink',
    RowsPerPageDropdown: (options) => {
      return (
        <React.Fragment>
          <span className="mx-1" style={{ color: 'var(--black-500)', userSelect: 'none' }}>
            Items Per Page
          </span>
          <Dropdown value={options.value} options={paginationDropdownOptions} onChange={onPageChange} disabled={files?.length === 0} />
        </React.Fragment>
      );
    },
    NextPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={nextChange}
          disabled={!nextToken && paginationDirection === 'forward'}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
    PrevPageLink: ({ iconClassName }) => {
      return (
        <Button
          onClick={previousChange}
          disabled={!transitionToken || (transitionToken && !nextToken && paginationDirection === 'reverse')}
          className={'p-paginator-next p-paginator-element p-link'}
          icon={iconClassName}
        />
      );
    },
  };
  const containsFolders = selectedRows.some((item) => item.entry_type === 'folder');
  async function downloadFiles() {
    try {
      const files = selectedRows?.filter((item) => item.entry_type !== 'folder');
      const folders = selectedRows?.filter((item) => item.entry_type === 'folder');

      // Download files
      if (files?.length > 0) {
        await Promise.all(
          files.map(async (file) => {
            await downloadFile(file, () => handleGetFiles(pageLimit, breadCrumbs?.[breadCrumbs.length - 1]?.value));
          })
        );
      }

      // Download folders
      if (folders?.length > 0) {
        await Promise.all(
          folders.map(async (folder) => {
            const caseId = folder.case_id || (isClientFileManager ? fileManagerFilters?.case?.id : caseDetails.case_id);
            await downloadFolder(folder, caseId);
          })
        );
      }

      console.log('All files and folders processed successfully');
      setSelectedRows([]);
    } catch (error) {
      console.error('Error downloading files:', error);
    }
  }

  async function markFilesAsRead() {
    try {
      const fileItems = selectedRows?.map((file) => ({
        pk: file?.pk,
        sk: file?.sk,
      }));

      const sendData = {
        file_items: fileItems,
        is_read: true,
      };

      await fileBulkMarkAsRead(sendData);
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Files marked as read successfully');
      handleGetFiles(pageLimit, breadCrumbs?.[breadCrumbs.length - 1]?.value);
      setSelectedRows([]);
    } catch (error) {
      console.error('Error marking files as read:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to mark files as read');
    }
  }

  const header = (
    <div className="flex flex-wrap align-items-end justify-content-end gap-2 d-flex files-buttons">
      {!isClientFileManager && !isClientFileManager && (
        <Button
          // variant="outline-secondary"
          className="p-button  p-button-primary ms-2"
          onClick={(e) => {
            setShowBulkDeleteModal(true);
          }}
        >
          <i className="icon-delete text-white me-2 pointer" aria-hidden="true" title="delete"></i>
          Delete
        </Button>
      )}

      {!isGlobalFileManager && !isClientFileManager && (
        <Button
          className="p-button  p-button-primary ms-2"
          onClick={() => {
            setShowBulkMoveModal(true);
            setIsCopy(false);
          }}
        >
          <i className="icon-move text-white me-2 pointer" aria-hidden="true" title="Move"></i>
          Move
        </Button>
      )}

      {!isGlobalFileManager && !isClientFileManager && (
        <Button
          className="p-button  p-button-primary ms-2"
          onClick={() => {
            setShowBulkMoveModal(true);
            setIsCopy(true);
          }}
        >
          <i className="icon-copy text-white me-2 pointer" aria-hidden="true" title="Copy"></i>
          Copy
        </Button>
      )}

      {!isGlobalFileManager && !isClientFileManager && (
        <Button label="Share" className="p-button  p-button-primary outline ms-2" onClick={() => setShowShareFileModal(true)} />
      )}
      <Button className="p-button  p-button-primary ms-2" onClick={downloadFiles}>
        <i className="icon-save text-white me-2 pointer" aria-hidden="true" title="Download"></i>Download
      </Button>
      {!containsFolders && (
        <Button className="p-button  p-button-primary ms-2" onClick={markFilesAsRead}>
          <i className="icon-check text-white me-2 pointer" aria-hidden="true" title="Mark as Read"></i>Mark as Read
        </Button>
      )}
    </div>
  );

  // Wrap the whole ListView in dropzone root
  return (
    <div
      className="overflow-auto bg-white shadow-middle mb-3"
      {...(!isFileManagerRoute ? getRootProps() : {})}
      style={{ position: 'relative' }}
    >
      <Tooltip target=".share" />
      {!isFileManagerRoute && isDragActive && (
        <div
          className="dropzone-overlay"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.25)',
            border: '2px dashed var(--primary-color, #0d6efd)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.25rem',
            zIndex: 1000,
            pointerEvents: 'none',
            borderRadius: '6px',
            backdropFilter: 'blur(1px)',
          }}
        >
          Drop files to upload
        </div>
      )}

      <ResizableDataTable
        header={selectedRows.length ? header : false}
        value={files}
        responsiveLayout="scroll"
        className="p-datatable-responsive file-sharing-details"
        emptyMessage="No Data Found."
        paginatorTemplate={PaginationTemplate}
        rows={pageLimit}
        stripedRows
        paginator
        resizableColumns={!isClientFileManager}
        columns={
          isGlobalFileManager
            ? (() => {
                // Define all Global File Manager Columns
                const allGlobalColumns = [
                  {
                    field: 'display_name',
                    header: 'File Name',
                    body: (rowData, event) => (
                      <>
                        <div className="w-100 d-flex align-items-center gap-2">
                          {/* Unread indicator dot */}
                          {rowData?.is_read === false && (
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--primary-main)',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {/* File name with bold text for unread files */}
                          <span
                            className="w-100"
                            style={{
                              fontWeight: rowData?.is_read === false ? 'bold' : 'normal',
                              color: rowData?.is_read === false ? 'var(--primary-dark)' : 'inherit',
                            }}
                          >
                            <TitleTemplate
                              rowData={rowData}
                              event={event}
                              isClientFileManager={isClientFileManager}
                              isGlobalFileManager={isGlobalFileManager}
                            />
                          </span>
                        </div>
                        {rowData?.tags && rowData.tags.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                            {rowData.tags.map((tag, index) => (
                              <span key={index} className="tag-grey">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    ),
                    sortable: true,
                    bodyClassName: 'fileManager-name',
                  },
                  {
                    field: 'case_name',
                    header: 'Case',
                    headerClassName: 'case-column-header',
                    body: (rowData) => <CaseTemplate rowData={rowData} />,
                    sortable: true,
                    bodyClassName: 'ellipsis-text',
                  },
                  {
                    field: 'user_full_name',
                    header: 'Uploaded By',
                    headerClassName: 'css-uploaded-by-column-header',
                    body: (rowData) => <UploadedByTemplate rowData={rowData} />,
                    sortable: true,
                    // bodyClassName: 'ellipsis-text',
                  },
                  {
                    field: 'time',
                    header: 'Date Created',
                    body: (rowData) => (
                      <UploadedTemplate userContext={userContext} rowData={rowData} isClientFileManager={isClientFileManager} />
                    ),
                    sortable: true,
                  },
                  {
                    field: 'updated',
                    header: 'Date Modified',
                    body: (rowData) => (
                      <LastEditedTemplate userContext={userContext} rowData={rowData} isClientFileManager={isClientFileManager} />
                    ),
                    sortable: true,
                  },
                  {
                    field: 'size',
                    header: 'Size',
                    headerClassName: 'css-size-column-header',
                    body: (rowData) => <SizeTemplate rowData={rowData} />,
                    sortable: true,
                    bodyClassName: 'ellipsis-text',
                  },
                  {
                    field: 'pages',
                    header: 'Pages',
                    headerClassName: 'css-pages-column-header',
                    body: (rowData) => <PagesTemplate rowData={rowData} />,
                    sortable: false,
                    bodyClassName: 'ellipsis-text',
                  },
                  {
                    field: 'esignature_status',
                    header: 'Signature Status',
                    headerClassName: 'signature-status-column-header',
                    body: (rowData) => (
                      <SignatureTemplate
                        rowData={rowData}
                        modals={modals}
                        setSelectedItem={setSelectedFile}
                        isClientFileManager={isClientFileManager}
                        isLeadFileManager={isLeadFileManager}
                        onSignClick={() => {
                          // You can add your sign logic here if needed
                        }}
                      />
                    ),
                    sortable: true,
                    bodyClassName: 'ellipsis-text',
                  },
                  {
                    header: 'Actions',
                    headerClassName: 'actions-column-header',
                    body: (rowData, e) => (
                      <div className="text-right d-flex w-100 align-items-center">
                        <GlobalActionsTemplate
                          rowData={rowData}
                          e={e}
                          itemsRef={itemsRef}
                          caseDetails={caseDetails}
                          onDownloadComplete={() => handleGetFiles(pageLimit, breadCrumbs?.[breadCrumbs.length - 1]?.value)}
                        />
                      </div>
                    ),
                    exportable: false,
                    className: 'fileManager-actions',
                    fixed: true,
                    width: '220px',
                    field: 'action',
                  },
                ];

                // Filter out Case and Signature Status columns for Firm Documents
                if (isFirmDocuments) {
                  return allGlobalColumns.filter((column) => column.field !== 'case_name' && column.field !== 'esignature_status');
                }

                // Return all columns for regular Global File Manager
                return allGlobalColumns;
              })()
            : [
                // Original Case File Manager Columns
                {
                  header: (
                    <HeaderCheckbox
                      handleSelectAllCheckboxClick={() => handleSelectAllCheckboxClick(files)}
                      selectAll={selectAll}
                      selectedRows={selectedRows}
                      files={files}
                    />
                  ),
                  body: (rowData) => (
                    <CheckboxTemplate handleSingleCheckboxClick={handleSingleCheckboxClick} selectedRows={selectedRows} rowData={rowData} />
                  ),
                  bodyClassName: 'ellipsis-text',
                  fixed: true,
                  width: '40px',
                  field: 'id',
                },
                {
                  field: 'display_name',
                  header: 'Title',
                  body: (rowData, event) => (
                    <TitleTemplate rowData={rowData} event={event} isClientFileManager={isClientFileManager} isGlobalFileManager={false} />
                  ),
                  sortable: true,
                  bodyClassName: 'fileManager-name',
                },
                {
                  field: 'size',
                  header: 'File Size',
                  body: (rowData) => <SizeTemplate rowData={rowData} />,
                  sortable: true,
                  bodyClassName: 'ellipsis-text',
                  width: '100px',
                },
                {
                  field: 'pages',
                  header: 'Pages',
                  body: (rowData) => <PagesTemplate rowData={rowData} />,
                  sortable: false,
                  bodyClassName: 'ellipsis-text',
                  width: '80px',
                },
                {
                  field: 'signature',
                  header: 'Signature',
                  body: (rowData) => <SignatureTemplate rowData={rowData} />,
                  bodyClassName: 'ellipsis-text',
                  hidden: !userContext.checkSubPermission('e_signature'),
                },
                {
                  field: 'time',
                  header: 'Date Uploaded',
                  body: (rowData) => (
                    <UploadedTemplate userContext={userContext} rowData={rowData} isClientFileManager={isClientFileManager} />
                  ),
                  sortable: true,
                },
                {
                  field: 'updated',
                  header: 'Last Edited',
                  body: (rowData) => (
                    <LastEditedTemplate userContext={userContext} rowData={rowData} isClientFileManager={isClientFileManager} />
                  ),
                  sortable: true,
                },
                {
                  field: 'Uploaded By',
                  header: 'Uploaded By',
                  body: (rowData) => <UploadedByTemplate rowData={rowData} />,
                  sortable: true,
                  bodyClassName: 'ellipsis-text',
                  width: '100px',
                },
                {
                  header: 'Shared',
                  body: (rowData, index) => (
                    <>
                      <span className="p-column-title text-break">Shared</span>
                      <span className="file-sharing-details">
                        {rowData?.entry_type === 'folder' ? (
                          // Folder sharing logic
                          rowData?.folder_shared === true ? (
                            <i
                              className="fas fa-check-circle pointer shared-icon share-details"
                              id={`shared-folder-tooltip-${rowData.sk}`}
                              onClick={() => {
                                setShowFileSharingDetailsModal(true);
                                setSelectedFile(rowData);
                              }}
                            >
                              <Tooltip
                                target={`#shared-folder-tooltip-${rowData.sk.replace(/[^\w-]/g, '\\$&')}`}
                                className="file-share-tooltip"
                                position="top"
                              >
                                <FileSharingDetailsCard rowData={rowData} />
                              </Tooltip>
                            </i>
                          ) : (
                            <i className="fas fa-times-circle" style={{ cursor: 'default' }}></i>
                          )
                        ) : // File sharing logic (existing)
                        isCurrentDatePastExpiry(rowData?.lawft_link_expires_on, userContext?.userDetails?.timezone) === false ||
                          rowData?.file_shared_contact_id?.length > 0 ? (
                          <i
                            className="fas fa-check-circle pointer shared-icon share-details"
                            id={`shared-file-tooltip-${rowData.sk}`}
                            onClick={() => {
                              setShowFileSharingDetailsModal(true);
                              setSelectedFile(rowData);
                            }}
                          >
                            <Tooltip
                              target={`#shared-file-tooltip-${rowData.sk.replace(/[^\w-]/g, '\\$&')}`}
                              className="file-share-tooltip"
                              position="top"
                            >
                              <FileSharingDetailsCard rowData={rowData} />
                            </Tooltip>
                          </i>
                        ) : (
                          <i className="fas fa-times-circle" style={{ cursor: 'default' }}></i>
                        )}
                      </span>
                    </>
                  ),
                  exportable: false,
                  hidden: isClientFileManager,
                  field: 'esign',
                },
                {
                  header: 'Actions',
                  body: (rowData, e) => (
                    <div className="text-right d-flex w-100 align-items-center">
                      <ActionsTemplate
                        rowData={rowData}
                        e={e}
                        itemsRef={itemsRef}
                        caseDetails={caseDetails}
                        onDownloadComplete={() => handleGetFiles(pageLimit, breadCrumbs?.[breadCrumbs.length - 1]?.value)}
                      />
                    </div>
                  ),
                  exportable: false,
                  className: 'fileManager-actions',
                  fixed: true,
                  width: '220px',
                  field: 'action',
                },
              ]
        }
        storageKey="file_manager_list"
      />
      <FileSharingDetailsModal
        show={
          showFileSharingDetailsModal &&
          (selectedFile?.lawft_link_expires_on || (selectedFile?.entry_type === 'folder' && selectedFile?.folder_shared === true))
        }
        setShow={showFileSharingDetailsModal}
        onHide={() => {
          setShowFileSharingDetailsModal(false);
        }}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
      />
    </div>
  );
}

export default ListView;
